// Rechnen und Bons der Kasse — ohne Oberfläche, damit es sich direkt testen lässt.
//
// Gerechnet wird genau wie im Backend (beleg.js → rechnen): Netto je Zeile auf
// Cent, Rabatt auf den Bon je Zeile, Steuer je Satz. So steht auf dem Knopf
// „Bezahlen" derselbe Betrag wie später auf dem Beleg — keinen Cent anders.
//
// Bewusst ohne Importe außer Typen.

export type Zeile = {
  id: string;
  produktId: string | null;
  bezeichnung: string;
  einheit: string;
  menge: number;
  /** Nettopreis je Einheit wie im Artikel (bis vier Stellen). */
  preis: number;
  steuersatz: number;
  /** Rabatt auf diese Zeile in Prozent. */
  rabatt: number;
  /** Gewicht, Länge … — Menge wird eingetippt statt gezählt. */
  teilbar: boolean;
  /** Lagerbestand beim Hinzufügen; null = frei (Dienstleistung, freier Betrag). */
  bestand: number | null;
};

export type Bon = {
  id: string;
  kundeId: string;
  zeilen: Zeile[];
  /** Rabatt auf den ganzen Bon in Prozent. */
  rabatt: number;
  angelegt: string;
};

const r2 = (n: number) => Math.round(n * 100) / 100;
const r4 = (n: number) => Math.round(n * 10000) / 10000;

export const neueId = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
export const leererBon = (): Bon => ({ id: neueId(), kundeId: '', zeilen: [], rabatt: 0, angelegt: new Date().toISOString() });

/** Nettopreis je Einheit nach Zeilenrabatt. */
export const einzelNetto = (z: Zeile) => r4(z.preis * (1 - (z.rabatt || 0) / 100));
/** Bruttopreis zur Anzeige (Kachel, Zeile). */
export const brutto = (netto: number, satz: number) => r2(netto * (1 + satz / 100));
/** Was der Kunde für eine Einheit bezahlt, in netto umgerechnet — für Preis ändern und freie Beträge. */
export const nettoAusBrutto = (b: number, satz: number) => r4(b / (1 + satz / 100));

/** Zeilenbetrag brutto zur Anzeige (vor Bonrabatt). */
export const zeilenBrutto = (z: Zeile) => brutto(r2(z.menge * einzelNetto(z)), z.steuersatz);

export type Summen = { netto: number; steuern: Array<{ satz: number; netto: number; steuer: number }>; steuer: number; brutto: number; ersparnis: number };

export function summen(bon: Bon): Summen {
  const faktor = 1 - (bon.rabatt || 0) / 100;
  const nachSatz = new Map<number, number>();
  let netto = 0;
  let ohneRabatt = 0;
  for (const z of bon.zeilen) {
    const zeile = r2(z.menge * einzelNetto(z));
    const n = r2(zeile * faktor);
    netto += n;
    ohneRabatt += brutto(r2(z.menge * z.preis), z.steuersatz);
    nachSatz.set(z.steuersatz, r2((nachSatz.get(z.steuersatz) || 0) + n));
  }
  netto = r2(netto);
  const steuern = [...nachSatz].map(([satz, n]) => ({ satz, netto: n, steuer: r2(n * (satz / 100)) }));
  const steuer = r2(steuern.reduce((s, x) => s + x.steuer, 0));
  const gesamt = r2(netto + steuer);
  return { netto, steuern, steuer, brutto: gesamt, ersparnis: Math.max(0, r2(ohneRabatt - gesamt)) };
}

/** Positionen fürs Backend — Zeilenrabatt steckt im Einzelpreis und steht in der Bezeichnung. */
export function positionenFuerApi(bon: Bon, rabattText = (p: number) => `−${p} %`) {
  return bon.zeilen.map(z => ({
    produktId: z.produktId,
    bezeichnung: z.rabatt ? `${z.bezeichnung} (${rabattText(z.rabatt)})` : z.bezeichnung,
    menge: z.menge,
    einheit: z.einheit,
    einzelpreis: einzelNetto(z),
    steuersatz: z.steuersatz,
  }));
}

/**
 * Schnellbeträge fürs Bargeld: „passend" und die nächsten runden Scheine
 * darüber — bei 13,40 € also 13,40 · 15 · 20 · 50.
 */
export function schnellbetraege(betrag: number): number[] {
  const werte = new Set<number>([r2(betrag)]);
  for (const stufe of [5, 10, 20, 50, 100, 200, 500]) {
    const w = Math.ceil(betrag / stufe) * stufe;
    if (w > betrag + 0.004) werte.add(w);
    if (werte.size >= 4) break;
  }
  return [...werte].sort((a, b) => a - b);
}

/** Wie viele Stück dürfen noch in den Bon — nie mehr, als im Lager liegt. */
export function nochMoeglich(bon: Bon, produktId: string, bestand: number): number {
  const drin = bon.zeilen.filter(z => z.produktId === produktId).reduce((s, z) => s + z.menge, 0);
  return Math.max(0, bestand - drin);
}

// ── Gespeicherte Bons (geparkt) ─────────────────────────────────────────────
// Im Browser des Geräts — eine Kasse ist ein Gerät. Fehlt der Speicher
// (privates Fenster), läuft die Kasse trotzdem, nur ohne Parken über einen
// Neustart hinaus.

export type Stand = { aktiv: string; bons: Bon[] };

export function standLesen(schluessel: string): Stand {
  try {
    const roh = localStorage.getItem(schluessel);
    if (roh) {
      const s = JSON.parse(roh) as Stand;
      if (Array.isArray(s.bons) && s.bons.length) return { aktiv: s.bons.some(b => b.id === s.aktiv) ? s.aktiv : s.bons[0].id, bons: s.bons };
    }
  } catch { /* egal */ }
  const b = leererBon();
  return { aktiv: b.id, bons: [b] };
}

export function standSchreiben(schluessel: string, s: Stand) {
  try { localStorage.setItem(schluessel, JSON.stringify(s)); } catch { /* egal */ }
}
